import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Save, Loader2, Copy, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { workflowAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const CHANNELS = ['in_app', 'email', 'whatsapp', 'sms', 'push'];
const STEP_TYPES = ['approval', 'task', 'notification'];
const APPROVER_MODES = [
  { value: 'seniority', label: 'Seniority (role or more senior)' },
  { value: 'any_of', label: 'Any of (role OR named user)' },
  { value: 'all_of', label: 'All of (role AND named user)' },
];
const ESC_ACTIONS = ['notify', 'reassign', 'auto_approve', 'auto_reject'];
const COND_OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'contains', 'exists'];

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const csv = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

const blankStep = (order) => ({
  name: '', type: 'approval', order,
  approverMode: 'seniority', roles: '', userIds: '', minRole: '',
  minApprovals: 1, optional: false, join: 'all',
  slaHours: '', remindEvery: '', remindMax: '',
  escAfter: '', escAction: 'notify', escToRoles: '',
  condField: '', condOp: 'gt', condValue: '',
  channels: ['in_app'],
});

// Compile the builder rows into the backend template `steps` contract.
const compileStep = (s, idx) => {
  const step = {
    key: slugify(s.name) || `step_${idx + 1}`,
    name: s.name || `Step ${idx + 1}`,
    type: s.type,
    order: Number(s.order) || idx + 1,
    join: s.join,
    optional: !!s.optional,
    min_approvals: Number(s.minApprovals) || 1,
  };
  const roles = csv(s.roles);
  const userIds = csv(s.userIds).map(Number).filter((n) => !Number.isNaN(n));
  if (s.approverMode === 'seniority') {
    if (s.minRole) step.approvers = { mode: 'seniority', min_role: s.minRole.trim() };
  } else if (roles.length || userIds.length || s.minRole) {
    step.approvers = { mode: s.approverMode, roles, user_ids: userIds };
    if (s.minRole) step.approvers.min_role = s.minRole.trim();
  }
  if (s.slaHours) step.sla_hours = Number(s.slaHours);
  if (s.remindEvery) step.reminders = { every_hours: Number(s.remindEvery), max: Number(s.remindMax) || 3 };
  if (s.escAfter || s.escToRoles) {
    step.escalation = {
      after_hours: numOrNull(s.escAfter),
      action: s.escAction,
      to_roles: csv(s.escToRoles),
    };
  }
  if (s.condField) {
    step.conditions = { field: s.condField.trim(), op: s.condOp, value: coerce(s.condValue) };
  }
  if (s.channels?.length) step.notify = { channels: s.channels, on: ['assigned', 'reminder', 'overdue', 'escalated'] };
  return step;
};

const coerce = (v) => {
  if (v === '' || v == null) return v;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
};

const StepEditor = ({ step, index, onChange, onRemove }) => {
  const [open, setOpen] = useState(true);
  const set = (patch) => onChange({ ...step, ...patch });
  const toggleChannel = (c) =>
    set({ channels: step.channels.includes(c) ? step.channels.filter((x) => x !== c) : [...step.channels, c] });

  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center justify-between gap-2 p-3 bg-muted/20">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-medium text-foreground">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          Step {index + 1}: {step.name || '(unnamed)'} <span className="text-xs text-muted-foreground">· order {step.order} · {step.type}</span>
        </button>
        <Button size="icon" variant="ghost" onClick={onRemove} title="Remove step"><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
      {open && (
        <div className="p-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Step name</Label>
            <Input value={step.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Finance Review" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label>Type</Label>
              <select className="wf-select" value={step.type} onChange={(e) => set({ type: e.target.value })}>
                {STEP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Order</Label>
              <Input type="number" value={step.order} onChange={(e) => set({ order: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Join</Label>
              <select className="wf-select" value={step.join} onChange={(e) => set({ join: e.target.value })}>
                <option value="all">all</option>
                <option value="any">any</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Approver mode</Label>
            <select className="wf-select" value={step.approverMode} onChange={(e) => set({ approverMode: e.target.value })}>
              {APPROVER_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label>Roles (csv)</Label>
              <Input value={step.roles} onChange={(e) => set({ roles: e.target.value })} placeholder="hr_head, finance_manager" disabled={step.approverMode === 'seniority'} />
            </div>
            <div className="space-y-1">
              <Label>User IDs (csv)</Label>
              <Input value={step.userIds} onChange={(e) => set({ userIds: e.target.value })} placeholder="42, 87" disabled={step.approverMode === 'seniority'} />
            </div>
            <div className="space-y-1">
              <Label>Min role (seniority)</Label>
              <Input value={step.minRole} onChange={(e) => set({ minRole: e.target.value })} placeholder="reporting_manager" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label>Min approvals</Label>
              <Input type="number" min="1" value={step.minApprovals} onChange={(e) => set({ minApprovals: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>SLA hours</Label>
              <Input type="number" value={step.slaHours} onChange={(e) => set({ slaHours: e.target.value })} placeholder="48" />
            </div>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input type="checkbox" checked={step.optional} onChange={(e) => set({ optional: e.target.checked })} /> Optional
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Remind every (h)</Label>
              <Input type="number" value={step.remindEvery} onChange={(e) => set({ remindEvery: e.target.value })} placeholder="12" />
            </div>
            <div className="space-y-1">
              <Label>Max reminders</Label>
              <Input type="number" value={step.remindMax} onChange={(e) => set({ remindMax: e.target.value })} placeholder="3" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label>Escalate after (h)</Label>
              <Input type="number" value={step.escAfter} onChange={(e) => set({ escAfter: e.target.value })} placeholder="72" />
            </div>
            <div className="space-y-1">
              <Label>Escalation action</Label>
              <select className="wf-select" value={step.escAction} onChange={(e) => set({ escAction: e.target.value })}>
                {ESC_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Escalate to roles</Label>
              <Input value={step.escToRoles} onChange={(e) => set({ escToRoles: e.target.value })} placeholder="dean" />
            </div>
          </div>

          <div className="sm:col-span-2 grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label>Condition field</Label>
              <Input value={step.condField} onChange={(e) => set({ condField: e.target.value })} placeholder="amount / department / location" />
            </div>
            <div className="space-y-1">
              <Label>Operator</Label>
              <select className="wf-select" value={step.condOp} onChange={(e) => set({ condOp: e.target.value })}>
                {COND_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Value</Label>
              <Input value={step.condValue} onChange={(e) => set({ condValue: e.target.value })} placeholder="1000" />
            </div>
          </div>

          <div className="sm:col-span-2 space-y-1">
            <Label>Notify channels</Label>
            <div className="flex flex-wrap gap-3">
              {CHANNELS.map((c) => (
                <label key={c} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={step.channels.includes(c)} onChange={() => toggleChannel(c)} /> {c}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WorkflowBuilderScreen = () => {
  const [custom, setCustom] = useState([]);
  const [base, setBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('general');
  const [steps, setSteps] = useState([blankStep(1)]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workflowAPI.listBuilderTemplates();
      setCustom(res?.data?.custom || []);
      setBase(res?.data?.base || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addStep = () => setSteps((s) => [...s, blankStep(s.length + 1)]);
  const updateStep = (i, next) => setSteps((s) => s.map((st, idx) => (idx === i ? next : st)));
  const removeStep = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) return toast.error('Give the workflow a name');
    if (!steps.length) return toast.error('Add at least one step');
    try {
      setSaving(true);
      await workflowAPI.saveBuilderTemplate({
        key: slugify(name),
        name: name.trim(),
        category: category || 'general',
        steps: steps.map(compileStep),
      });
      toast.success('Workflow template saved');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save template'));
    } finally {
      setSaving(false);
    }
  };

  const editExisting = (t) => {
    // Load a saved template back into the editor (best-effort decompile).
    setName(t.name);
    setCategory(t.category || 'general');
    const def = t.definition || {};
    setSteps((def.steps || []).map((s, i) => ({
      ...blankStep(i + 1),
      name: s.name || '',
      type: s.type || 'approval',
      order: s.order ?? i + 1,
      join: s.join || 'all',
      optional: !!s.optional,
      minApprovals: s.min_approvals || 1,
      approverMode: s.approvers?.mode || (s.approvers ? 'any_of' : 'seniority'),
      roles: (s.approvers?.roles || []).join(', '),
      userIds: (s.approvers?.user_ids || []).join(', '),
      minRole: s.approvers?.min_role || s.assignee_role || '',
      slaHours: s.sla_hours ?? '',
      remindEvery: s.reminders?.every_hours ?? '',
      remindMax: s.reminders?.max ?? '',
      escAfter: s.escalation?.after_hours ?? '',
      escAction: s.escalation?.action || 'notify',
      escToRoles: (s.escalation?.to_roles || []).join(', '),
      condField: s.conditions?.field || '',
      condOp: s.conditions?.op || 'gt',
      condValue: s.conditions?.value ?? '',
      channels: s.notify?.channels || ['in_app'],
    })));
    toast.info(`Editing "${t.name}"`);
  };

  const remove = async (key) => {
    try {
      await workflowAPI.deleteBuilderTemplate(key);
      toast.success('Template deleted');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not delete'));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <style>{`.wf-select{display:flex;height:2.5rem;width:100%;border-radius:0.375rem;border:1px solid hsl(var(--input));background:hsl(var(--background));padding:0 0.75rem;font-size:0.875rem}`}</style>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground">Workflow Builder</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Every approval rule below is stored as configuration — approvers, SLAs, escalation, parallel branches,
            conditions and notifications. No code changes are needed to add or change a workflow.
          </p>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Workflow name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Capital Expenditure Approval" />
              {name && <p className="text-xs text-muted-foreground">key: {slugify(name)}</p>}
            </div>
            <div className="space-y-1">
              <Label>Category</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="finance" />
            </div>
          </div>

          <div className="space-y-3">
            {steps.map((s, i) => (
              <StepEditor key={i} step={s} index={i} onChange={(n) => updateStep(i, n)} onRemove={() => removeStep(i)} />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={addStep}><Plus className="h-4 w-4 mr-1" /> Add step</Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save template</>}
            </Button>
            <p className="text-xs text-muted-foreground">Tip: give two steps the same <b>order</b> to run them in parallel.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Saved templates</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : custom.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No custom templates yet. Build one above. ({base.length} built-in templates available as a base.)</p>
          ) : (
            <div className="space-y-2">
              {custom.map((t) => (
                <div key={t.key} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{t.name} {t.global && <span className="text-xs text-primary">· global</span>}</p>
                    <p className="text-xs text-muted-foreground">{(t.definition?.steps || []).length} steps · {t.category}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => editExisting(t)}><Copy className="h-4 w-4 mr-1" /> Edit</Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(t.key)} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkflowBuilderScreen;
